import { useCallback, useEffect, useMemo, useState } from "react";
import { DivIcon } from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from "react-leaflet";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CloudRain, CloudSnow, Crosshair, Loader2, MapPin, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const createPinIcon = (type = "rain") => new DivIcon({
  className: "rain-snow-tracker-pin-icon",
  html: `
    <div class="rain-snow-tracker-pin ${type === "new" ? "pin-new" : type === "snow" ? "pin-snow" : "pin-rain"}">
      <span>${type === "new" ? "+" : type === "snow" ? "❄" : "☔"}</span>
    </div>
  `,
  iconSize: [34, 42],
  iconAnchor: [17, 42],
  popupAnchor: [0, -38],
});

const rainPinIcon = createPinIcon("rain");
const snowPinIcon = createPinIcon("snow");
const newPinIcon = createPinIcon("new");

function ClickToPin({ onPick }) {
  useMapEvents({
    click(event) {
      onPick({ lat: event.latlng.lat, lon: event.latlng.lng });
    },
  });
  return null;
}

function totalLabel(value) {
  return `${Number(value || 0).toFixed(2)} in`;
}

export default function PrecipPinTracker({ location, savedLocations = [], isDarkMode = true }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pinName, setPinName] = useState("");
  const [adding, setAdding] = useState(false);

  const center = useMemo(() => [location?.lat || 35.2098, location?.lon || -86.108], [location]);

  const fetchPins = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/weather/precip-pins/totals`);
      setPins(response.data.pins || []);
    } catch (error) {
      toast.error("Unable to load tracker locations");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPins();
    const interval = setInterval(fetchPins, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPins]);

  const beginAddFromPoint = (point) => {
    setSelectedPoint(point);
    setPinName(point.name || "");
  };

  const addPin = async () => {
    if (!selectedPoint) {
      toast.error("Tap the map or choose a location first");
      return;
    }
    if (!pinName.trim()) {
      toast.error("Name this tracking location");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/weather/precip-pins`, {
        name: pinName.trim(),
        lat: selectedPoint.lat,
        lon: selectedPoint.lon,
      });
      toast.success("Tracking location added");
      setSelectedPoint(null);
      setPinName("");
      await fetchPins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add rain pin");
    } finally {
      setAdding(false);
    }
  };

  const deletePin = async (pinId) => {
    try {
      await axios.delete(`${API}/weather/precip-pins/${pinId}`);
      toast.success("Tracking location removed");
      await fetchPins();
    } catch (error) {
      toast.error("Unable to remove rain pin");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl p-4 md:p-5 ${isDarkMode ? "glass" : "bg-white/90 border border-gray-200"}`}
      data-testid="precip-pin-tracker"
    >
      <style>{`
        .rain-snow-tracker-pin-icon {
          background: transparent;
          border: 0;
        }
        .rain-snow-tracker-pin {
          width: 34px;
          height: 34px;
          border-radius: 50% 50% 50% 4px;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(255,255,255,0.96);
          box-shadow: 0 10px 22px rgba(0,0,0,0.25), 0 0 0 4px rgba(0,242,234,0.16);
        }
        .rain-snow-tracker-pin span {
          transform: rotate(45deg);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 900;
          line-height: 1;
          color: #031314;
        }
        .rain-snow-tracker-pin.pin-rain {
          background: linear-gradient(135deg, #00f2ea, #37b7ff);
        }
        .rain-snow-tracker-pin.pin-snow {
          background: linear-gradient(135deg, #bfdbfe, #60a5fa);
        }
        .rain-snow-tracker-pin.pin-new {
          background: linear-gradient(135deg, #ccff00, #00f2ea);
          animation: rainSnowPinPulse 1.4s ease-in-out infinite;
        }
        @keyframes rainSnowPinPulse {
          0%, 100% { box-shadow: 0 10px 22px rgba(0,0,0,0.25), 0 0 0 4px rgba(204,255,0,0.18); }
          50% { box-shadow: 0 10px 22px rgba(0,0,0,0.25), 0 0 0 8px rgba(204,255,0,0.08); }
        }
      `}</style>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-[#00f2ea]/10">
              <MapPin className="w-5 h-5 text-[#00f2ea]" />
            </div>
            <h3 className="font-bold text-lg text-white/90" data-testid="rain-snow-tracker-title">Rain/Snow Tracker</h3>
          </div>
          <p className="text-xs text-white/45 mt-1">Tap the map to track exact rain and snow totals — 24h, 72h, 7d, MTD, and YTD.</p>
        </div>
        <Button onClick={fetchPins} disabled={refreshing} variant="ghost" className="self-start md:self-auto" data-testid="precip-pins-refresh-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[320px]" data-testid="precip-pins-map-panel">
          <MapContainer center={center} zoom={9} scrollWheelZoom={false} className="h-[320px] w-full z-0">
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <ClickToPin onPick={beginAddFromPoint} />
            {selectedPoint && (
              <Marker position={[selectedPoint.lat, selectedPoint.lon]} icon={newPinIcon}>
                <Popup>New tracking location</Popup>
              </Marker>
            )}
            {pins.map((pin) => (
              <Marker key={pin.id} position={[pin.lat, pin.lon]} icon={(pin.current?.snow_1h_in || pin.totals?.year_snow_in || 0) > 0 ? snowPinIcon : rainPinIcon}>
                <Popup>
                  <strong>{pin.name}</strong><br />
                  24h: {totalLabel(pin.totals?.last_24h_in)}<br />
                  7d: {totalLabel(pin.totals?.last_7d_in)}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="space-y-3 min-w-0" data-testid="rain-snow-tracker-panel">
          <div className={`rounded-2xl p-3 ${isDarkMode ? "bg-white/[0.04]" : "bg-gray-50"}`} data-testid="precip-pin-add-panel">
            <div className="flex flex-wrap gap-2 mb-3">
              {location && (
                <Button size="sm" variant="outline" onClick={() => beginAddFromPoint(location)} data-testid="precip-pin-use-current-button" className="border-white/15 text-white hover:bg-white/10">
                  <Crosshair className="w-4 h-4 mr-1" /> Current
                </Button>
              )}
              {savedLocations.slice(0, 4).map((loc) => (
                <Button key={loc.id} size="sm" variant="outline" onClick={() => beginAddFromPoint(loc)} data-testid={`precip-pin-use-saved-${loc.id}`} className="border-white/15 text-white hover:bg-white/10">
                  {loc.name}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <Input
                value={pinName}
                onChange={(e) => setPinName(e.target.value)}
                placeholder={selectedPoint ? `Name pin at ${selectedPoint.lat.toFixed(3)}, ${selectedPoint.lon.toFixed(3)}` : "Tap map or choose saved location"}
                data-testid="precip-pin-name-input"
                className="bg-black/25 border-white/10"
              />
              <Button onClick={addPin} disabled={adding || !selectedPoint} data-testid="precip-pin-add-button" className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]">
                {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Pin
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="rounded-2xl bg-white/[0.04] p-4" data-testid="precip-pins-loading">
              <Loader2 className="w-5 h-5 animate-spin text-[#00f2ea]" />
            </div>
          ) : pins.length === 0 ? (
            <div className="rounded-2xl bg-white/[0.04] p-4 text-sm text-white/55" data-testid="precip-pins-empty">
              No tracking locations yet. Add your home, fields, gardens, trailheads, or lake spots.
            </div>
          ) : (
            <div className="overflow-x-auto pb-2 snap-x snap-mandatory" data-testid="precip-pins-list">
              <div className="flex gap-3 w-max max-w-full">
              {pins.map((pin) => {
                const isSnow = (pin.current?.snow_1h_in || 0) > 0 || (pin.totals?.year_snow_in || 0) > 0;
                const Icon = isSnow ? CloudSnow : CloudRain;
                return (
                  <div key={pin.id} className="rounded-2xl bg-white/[0.045] border border-white/[0.07] p-4 w-[280px] sm:w-[320px] shrink-0 snap-start" data-testid={`precip-pin-card-${pin.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isSnow ? "text-blue-300" : "text-[#00f2ea]"}`} />
                          <h4 className="font-bold truncate">{pin.name}</h4>
                        </div>
                        <p className="text-[10px] text-white/35 mt-1">{pin.current?.condition} • Updated {new Date(pin.updated_at).toLocaleTimeString()}</p>
                      </div>
                      <button onClick={() => deletePin(pin.id)} className="p-2 rounded-lg hover:bg-red-500/15" data-testid={`precip-pin-delete-${pin.id}`}>
                        <Trash2 className="w-4 h-4 text-red-300" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <Metric label="24h" value={totalLabel(pin.totals?.last_24h_in)} testId={`precip-pin-24h-${pin.id}`} />
                      <Metric label="72h" value={totalLabel(pin.totals?.last_72h_in)} testId={`precip-pin-72h-${pin.id}`} />
                      <Metric label="7d" value={totalLabel(pin.totals?.last_7d_in)} testId={`precip-pin-7d-${pin.id}`} />
                      <Metric label="MTD" value={totalLabel(pin.totals?.month_to_date_in)} testId={`precip-pin-mtd-${pin.id}`} />
                      <Metric label="YTD" value={totalLabel(pin.totals?.year_to_date_in)} testId={`precip-pin-ytd-${pin.id}`} />
                      <Metric label="Now" value={pin.current?.is_precipitating ? totalLabel((pin.current?.rain_1h_in || 0) + (pin.current?.snow_1h_in || 0)) : "Dry"} testId={`precip-pin-now-${pin.id}`} />
                    </div>
                    <p className="text-[10px] text-white/30 mt-3">Source: {pin.source}</p>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-black/20 p-2 min-w-0" data-testid={testId}>
      <p className="text-[10px] text-white/35 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-black text-white truncate">{value}</p>
    </div>
  );
}